import React, { useState, useEffect } from 'react';
import {
  TrainingCourse,
  TrainingRequirement,
  TrainingAssignment,
  TrainingRecord,
  PersonQualification,
  CurrencyRecord,
  TrainingDashboardKpis,
  TrainingEffectivenessAssessment,
} from './types';
import { trainingService } from './trainingService';
import { TrainingMetricsCards } from './components/TrainingMetricsCards';
import { CourseCreateModal } from './components/CourseCreateModal';
import { AssignmentCreateModal } from './components/AssignmentCreateModal';
import { CbtLearningHubTab } from './components/CbtLearningHubTab';
import { useDialog } from '../../shared/DialogContext';
import { TablePagination, useTablePagination } from '../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../shared/useTableSort';

export const TrainingPage: React.FC = () => {
  const { showError, showSuccess } = useDialog();
  const [activeTab, setActiveTab] = useState<'matrix' | 'courses' | 'assignments' | 'records' | 'currency' | 'effectiveness' | 'cbt'>('matrix');
  const [kpis, setKpis] = useState<TrainingDashboardKpis | null>(null);
  const [requirements, setRequirements] = useState<TrainingRequirement[]>([]);
  const [courses, setCourses] = useState<TrainingCourse[]>([]);
  const [assignments, setAssignments] = useState<TrainingAssignment[]>([]);
  const [records, setRecords] = useState<TrainingRecord[]>([]);
  const [currencies, setCurrencies] = useState<CurrencyRecord[]>([]);
  const [qualifications, setQualifications] = useState<PersonQualification[]>([]);
  const [assessments, setAssessments] = useState<TrainingEffectivenessAssessment[]>([]);

  // Modals
  const [isCourseModalOpen, setIsCourseModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  const [loading, setLoading] = useState(true);

  const { sortedItems: sortedRequirements, sortField: reqSortField, sortDirection: reqSortDir, toggleSort: toggleReqSort } = useTableSort(requirements);
  const { sortedItems: sortedAssignments, sortField: asgSortField, sortDirection: asgSortDir, toggleSort: toggleAsgSort } = useTableSort(assignments);
  const { sortedItems: sortedRecords, sortField: recSortField, sortDirection: recSortDir, toggleSort: toggleRecSort } = useTableSort(records);

  const {
    currentPage: asgPage,
    setCurrentPage: setAsgPage,
    pageSize: asgPageSize,
    setPageSize: setAsgPageSize,
    totalItems: totalAsgItems,
    paginatedItems: paginatedAssignments,
  } = useTablePagination(sortedAssignments, 25);

  const {
    currentPage: recPage,
    setCurrentPage: setRecPage,
    pageSize: recPageSize,
    setPageSize: setRecPageSize,
    totalItems: totalRecItems,
    paginatedItems: paginatedRecords,
  } = useTablePagination(sortedRecords, 25);

  const {
    currentPage: reqPage,
    setCurrentPage: setReqPage,
    pageSize: reqPageSize,
    setPageSize: setReqPageSize,
    totalItems: totalReqItems,
    paginatedItems: paginatedRequirements,
  } = useTablePagination(sortedRequirements, 25);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [kpiData, reqData, crsData, asgData, recData, curData, qualData, effData] = await Promise.all([
        trainingService.getDashboardKpis(),
        trainingService.getRequirements(),
        trainingService.getCourses(),
        trainingService.getAssignments(),
        trainingService.getRecords(),
        trainingService.getCurrencies(),
        trainingService.getQualifications(),
        trainingService.getEffectivenessAssessments(),
      ]);
      setKpis(kpiData);
      setRequirements(reqData);
      setCourses(crsData);
      setAssignments(asgData);
      setRecords(recData);
      setCurrencies(curData);
      setQualifications(qualData);
      setAssessments(effData);
    } catch (err) {
      console.error('Error loading training data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCourse = async (data: any) => {
    await trainingService.createCourse(data);
    await loadAll();
  };

  const handleCreateAssignment = async (data: any) => {
    await trainingService.createAssignment(data);
    await loadAll();
  };

  const handleCompleteAssignment = async (assignment: TrainingAssignment) => {
    try {
      await trainingService.recordCompletion({
        assignment_id: assignment.id,
        person_id: assignment.person_id,
        person_name: assignment.person_name,
        person_department: assignment.person_department,
        course_id: assignment.course_id,
        completion_date: new Date().toISOString().slice(0, 10),
        result: 'PASSED',
        score: 95.0,
        instructor_name: 'Authorized Instructor',
        remarks: 'Completed and verified via digital learning platform.',
      });
      await loadAll();
      showSuccess('Training record created and marked as passed.');
    } catch (e: any) {
      showError(e, 'Training Completion Failed');
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="page-header-banner">
        <div className="badge">TRAINING & QUALIFICATIONS</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1>Training, Qualifications & Currency</h1>
            <p>EASA Part-ORO & ICAO SMS compliant training requirements, course catalog, personnel records, and recurrent currency tracking.</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setIsCourseModalOpen(true)}
              className="btn-secondary"
              style={{ width: 'auto' }}
            >
              + Define New Course
            </button>
            <button
              onClick={() => setIsAssignModalOpen(true)}
              className="btn-primary"
              style={{ width: 'auto', padding: '10px 18px' }}
            >
              + Assign Training
            </button>
          </div>
        </div>
      </div>

      {/* KPI Dashboard */}
      <TrainingMetricsCards kpis={kpis} />

      {/* Sub Tabs */}
      <div className="flex border-b border-slate-200 space-x-6 text-sm font-semibold overflow-x-auto">
        <button
          onClick={() => setActiveTab('matrix')}
          className={`pb-3 border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'matrix' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Gereksinimler (The WHY - {requirements.length})
        </button>
        <button
          onClick={() => setActiveTab('courses')}
          className={`pb-3 border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'courses' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Kurs Katalogu (The WHAT - {courses.length})
        </button>
        <button
          onClick={() => setActiveTab('assignments')}
          className={`pb-3 border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'assignments' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Personel Atamalari (The WHO - {assignments.length})
        </button>
        <button
          onClick={() => setActiveTab('records')}
          className={`pb-3 border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'records' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Egitim Sicil Kayitlari ({records.length})
        </button>
        <button
          onClick={() => setActiveTab('currency')}
          className={`pb-3 border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'currency' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Tazelik & Yetkiler (Currency - {currencies.length})
        </button>
        <button
          onClick={() => setActiveTab('effectiveness')}
          className={`pb-3 border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'effectiveness' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Egitim Etkinligi Degerlendirmesi ({assessments.length})
        </button>
        <button
          onClick={() => setActiveTab('cbt')}
          className={`pb-3 border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'cbt' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          🖥️ Digital CBT & Anti-Cheat Hub
        </button>
      </div>

      {/* Tab Contents */}
      {activeTab === 'matrix' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 bg-slate-50 border-b border-slate-200 font-semibold text-xs text-slate-600 uppercase tracking-wider">
            Zorunlu ve Operasyonel Egitim Gereksinimleri (Training Requirements)
          </div>
          <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500 uppercase sticky top-0 z-10">
                <tr>
                  <SortableTh field="requirement_code" currentField={reqSortField} currentDirection={reqSortDir} onSort={toggleReqSort} className="py-3 px-4">
                    Kod
                  </SortableTh>
                  <SortableTh field="title" currentField={reqSortField} currentDirection={reqSortDir} onSort={toggleReqSort} className="py-3 px-4">
                    Gereksinim Basligi & Kapsam
                  </SortableTh>
                  <SortableTh field="source_type" currentField={reqSortField} currentDirection={reqSortDir} onSort={toggleReqSort} className="py-3 px-4">
                    Kaynak (Source)
                  </SortableTh>
                  <SortableTh field="frequency_type" currentField={reqSortField} currentDirection={reqSortDir} onSort={toggleReqSort} className="py-3 px-4">
                    Siklik (Frequency)
                  </SortableTh>
                  <SortableTh field="target_department_name" currentField={reqSortField} currentDirection={reqSortDir} onSort={toggleReqSort} className="py-3 px-4">
                    Departman
                  </SortableTh>
                  <SortableTh field="is_mandatory" currentField={reqSortField} currentDirection={reqSortDir} onSort={toggleReqSort} className="py-3 px-4">
                    Zorunlu
                  </SortableTh>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedRequirements.map((req) => (
                  <tr key={req.id} className="hover:bg-slate-50">
                    <td className="py-3.5 px-4 font-mono font-bold text-indigo-700">{req.requirement_code}</td>
                    <td className="py-3.5 px-4">
                      <div className="font-semibold text-slate-900">{req.title}</div>
                      <div className="text-xs text-slate-500">{req.description}</div>
                    </td>
                    <td className="py-3.5 px-4">
                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800">
                        {req.source_type}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-xs font-medium text-slate-700">
                      {req.frequency_type} ({req.recurrence_interval_months} Ay)
                    </td>
                    <td className="py-3.5 px-4 text-slate-600 text-xs">{req.target_department_name}</td>
                    <td className="py-3.5 px-4">
                      {req.is_mandatory ? (
                        <span className="text-rose-600 font-bold text-xs">✓ ZORUNLU</span>
                      ) : (
                        <span className="text-slate-400 text-xs">Optional</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TablePagination
            currentPage={reqPage}
            totalItems={totalReqItems}
            pageSize={reqPageSize}
            onPageChange={setReqPage}
            onPageSizeChange={setReqPageSize}
            itemLabel="gereksinim"
          />
        </div>
      )}

      {activeTab === 'courses' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {courses.map((crs) => (
            <div key={crs.id} className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 flex flex-col justify-between">
              <div>
                <div className="flex justify-between items-start mb-2">
                  <span className="font-mono text-xs font-bold text-indigo-700 px-2 py-0.5 rounded bg-indigo-50 border border-indigo-200">
                    {crs.course_code}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-semibold">
                    {crs.category}
                  </span>
                </div>
                <h3 className="font-bold text-slate-900 text-base mb-1">{crs.title}</h3>
                <p className="text-xs text-slate-500 mb-4 line-clamp-2">{crs.description}</p>
              </div>

              <div className="pt-3 border-t border-slate-100 text-xs text-slate-600 space-y-1">
                <div className="flex justify-between">
                  <span>Metot:</span>
                  <span className="font-semibold">{crs.delivery_method}</span>
                </div>
                <div className="flex justify-between">
                  <span>Sure & Baraj:</span>
                  <span className="font-semibold">{crs.duration_minutes} Dk • %{crs.passing_score}</span>
                </div>
                <div className="flex justify-between">
                  <span>Gecerlilik:</span>
                  <span className="font-semibold">{crs.validity_days} Gun (Tazelik Kurali Aktif)</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'assignments' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 bg-slate-50 border-b border-slate-200 font-semibold text-xs text-slate-600 uppercase tracking-wider">
            Personel Egitim Atama ve Durum Cizelgesi (Assignments)
          </div>
          <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500 uppercase sticky top-0 z-10">
                <tr>
                  <SortableTh field="assignment_number" currentField={asgSortField} currentDirection={asgSortDir} onSort={toggleAsgSort} className="py-3 px-4">
                    Atama No
                  </SortableTh>
                  <SortableTh field="person_name" currentField={asgSortField} currentDirection={asgSortDir} onSort={toggleAsgSort} className="py-3 px-4">
                    Personel
                  </SortableTh>
                  <SortableTh field="course_title" currentField={asgSortField} currentDirection={asgSortDir} onSort={toggleAsgSort} className="py-3 px-4">
                    Kurs / Egitim
                  </SortableTh>
                  <SortableTh field="due_at" currentField={asgSortField} currentDirection={asgSortDir} onSort={toggleAsgSort} className="py-3 px-4">
                    Son Tarih
                  </SortableTh>
                  <SortableTh field="status" currentField={asgSortField} currentDirection={asgSortDir} onSort={toggleAsgSort} className="py-3 px-4">
                    Durum
                  </SortableTh>
                  <th className="py-3 px-4 text-right">Aksiyon</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedAssignments.map((asn) => (
                  <tr key={asn.id} className="hover:bg-slate-50">
                    <td className="py-3.5 px-4 font-mono font-bold text-indigo-700">{asn.assignment_number}</td>
                    <td className="py-3.5 px-4">
                      <div className="font-semibold text-slate-900">{asn.person_name}</div>
                      <div className="text-xs text-slate-500">{asn.person_department}</div>
                    </td>
                    <td className="py-3.5 px-4 font-medium text-slate-800">{asn.course_title || 'Egitim Kursu'}</td>
                    <td className="py-3.5 px-4 text-xs text-slate-600">{asn.due_at ? asn.due_at.slice(0, 10) : '-'}</td>
                    <td className="py-3.5 px-4">
                      {asn.status === 'COMPLETED' ? (
                        <span className="px-2.5 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-800">
                          TAMAMLANDI
                        </span>
                      ) : (
                        <span className="px-2.5 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800">
                          PENDING ({asn.priority})
                        </span>
                      )}
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      {asn.status !== 'COMPLETED' && (
                        <button
                          onClick={() => handleCompleteAssignment(asn)}
                          className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg shadow-sm"
                        >
                          Tamamla & Belgele
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TablePagination
            currentPage={asgPage}
            totalItems={totalAsgItems}
            pageSize={asgPageSize}
            onPageChange={setAsgPage}
            onPageSizeChange={setAsgPageSize}
            itemLabel="atama"
          />
        </div>
      )}

      {activeTab === 'records' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 bg-slate-50 border-b border-slate-200 font-semibold text-xs text-slate-600 uppercase tracking-wider">
            Resmi Egitim Tamamlama ve Sicil Kayitlari (Formal Proof / Evidence)
          </div>
          <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-500 uppercase sticky top-0 z-10">
                <tr>
                  <SortableTh field="record_number" currentField={recSortField} currentDirection={recSortDir} onSort={toggleRecSort} className="py-3 px-4">
                    Sicil No
                  </SortableTh>
                  <SortableTh field="person_name" currentField={recSortField} currentDirection={recSortDir} onSort={toggleRecSort} className="py-3 px-4">
                    Personel
                  </SortableTh>
                  <SortableTh field="course_title" currentField={recSortField} currentDirection={recSortDir} onSort={toggleRecSort} className="py-3 px-4">
                    Egitim Basligi
                  </SortableTh>
                  <SortableTh field="completion_date" currentField={recSortField} currentDirection={recSortDir} onSort={toggleRecSort} className="py-3 px-4">
                    Tamamlama Tarihi
                  </SortableTh>
                  <SortableTh field="result" currentField={recSortField} currentDirection={recSortDir} onSort={toggleRecSort} className="py-3 px-4">
                    Sonuc & Not
                  </SortableTh>
                  <SortableTh field="certificate_number" currentField={recSortField} currentDirection={recSortDir} onSort={toggleRecSort} className="py-3 px-4">
                    Sertifika No
                  </SortableTh>
                  <SortableTh field="valid_to" currentField={recSortField} currentDirection={recSortDir} onSort={toggleRecSort} className="py-3 px-4">
                    Gecerlilik Bitis
                  </SortableTh>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedRecords.map((rec) => (
                  <tr key={rec.id} className="hover:bg-slate-50">
                    <td className="py-3.5 px-4 font-mono font-bold text-indigo-700">{rec.record_number}</td>
                    <td className="py-3.5 px-4 font-semibold text-slate-900">{rec.person_name}</td>
                    <td className="py-3.5 px-4 text-slate-800">{rec.course_title}</td>
                    <td className="py-3.5 px-4 text-xs text-slate-600">{rec.completion_date.slice(0, 10)}</td>
                    <td className="py-3.5 px-4">
                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-800">
                        {rec.result} (%{rec.score})
                      </span>
                    </td>
                    <td className="py-3.5 px-4 font-mono text-xs text-slate-600">{rec.certificate_number || '-'}</td>
                    <td className="py-3.5 px-4 text-xs font-semibold text-indigo-700">{rec.valid_to ? rec.valid_to.slice(0, 10) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TablePagination
            currentPage={recPage}
            totalItems={totalRecItems}
            pageSize={recPageSize}
            onPageChange={setRecPage}
            onPageSizeChange={setRecPageSize}
            itemLabel="sicil kaydı"
          />
        </div>
      )}

      {activeTab === 'currency' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-4 bg-slate-50 border-b border-slate-200 font-semibold text-xs text-slate-600 uppercase tracking-wider">
              Personel Tazelik Durumu (Currency / Recency Status)
            </div>
            <div className="divide-y divide-slate-100">
              {currencies.map((cur) => (
                <div key={cur.id} className="p-4 flex justify-between items-center text-sm">
                  <div>
                    <div className="font-bold text-slate-900">{cur.person_name}</div>
                    <div className="text-xs text-slate-500">
                      Tazelik Kurali: {cur.rule_title || 'Yillik Periyodik Gecerlilik'}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="px-2.5 py-1 rounded text-xs font-bold bg-emerald-100 text-emerald-800">
                      {cur.status}
                    </span>
                    <div className="text-xs text-slate-500 mt-1">
                      Bitis: {cur.expires_at ? cur.expires_at.slice(0, 10) : '-'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-4 bg-slate-50 border-b border-slate-200 font-semibold text-xs text-slate-600 uppercase tracking-wider">
              Resmi Yetkiler ve Derecelendirmeler (Person Qualifications)
            </div>
            <div className="divide-y divide-slate-100">
              {qualifications.map((q) => (
                <div key={q.id} className="p-4 flex justify-between items-center text-sm">
                  <div>
                    <div className="font-bold text-slate-900">{q.person_name}</div>
                    <div className="text-xs text-slate-600 font-medium">{q.qualification_title}</div>
                    <div className="text-xs font-mono text-slate-400">Yetki Belge No: {q.qualification_number} ({q.issuing_authority})</div>
                  </div>
                  <div className="text-right">
                    <span className="px-2.5 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-800">
                      {q.status}
                    </span>
                    <div className="text-xs text-slate-500 mt-1">Gecerlilik: {q.expiry_date ? q.expiry_date.slice(0, 10) : 'Suresiz'}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'effectiveness' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 bg-slate-50 border-b border-slate-200 font-semibold text-xs text-slate-600 uppercase tracking-wider">
            Egitim Etkinligi ve Operasyonel Performans Degerlendirmeleri (TRAIN-106)
          </div>
          <div className="divide-y divide-slate-100">
            {assessments.map((eff) => (
              <div key={eff.id} className="p-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold text-slate-900">{eff.person_name}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">{eff.assessment_method}</span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">{eff.conclusion}</p>
                  <div className="text-xs text-slate-400 mt-1">
                    Onceki Bulgular: {eff.findings_before} &rarr; Sonraki Bulgular: <span className="font-bold text-emerald-600">{eff.findings_after}</span>
                  </div>
                </div>
                <div className="text-right">
                  <span className="px-3 py-1 rounded text-xs font-bold bg-emerald-100 text-emerald-800">
                    {eff.result} (%{eff.score})
                  </span>
                  <div className="text-xs text-slate-400 mt-1">Degerlendiren: {eff.assessed_by_name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'cbt' && <CbtLearningHubTab />}

      {/* Modals */}
      <CourseCreateModal
        requirements={requirements}
        isOpen={isCourseModalOpen}
        onClose={() => setIsCourseModalOpen(false)}
        onSubmit={handleCreateCourse}
      />

      <AssignmentCreateModal
        courses={courses}
        isOpen={isAssignModalOpen}
        onClose={() => setIsAssignModalOpen(false)}
        onSubmit={handleCreateAssignment}
      />
    </div>
  );
};
